import { AnalyticsContext } from '@providers/analyticsProvider';
import { useContext } from 'react';
import { ovAnalyticsEvents } from './analytics';
import { useAnalyticsContext } from './contextDetection';
import type { AnalyticsMeta } from './types';

/**
 * Hook for tracking edit-related analytics events
 * Handles both modal-based editing and navigation-based routing
 */
export const useEditAnalytics = () => {
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { pageContext } = useAnalyticsContext();

  /**
   * Track when edit functionality is initiated
   * Use this when:
   * - Table row is clicked to open edit modal
   * - Edit button is clicked
   * - Navigation to edit page occurs
   */
  const trackEditInit = (
    overrideType?: string,
    context?: string,
    additionalData?: AnalyticsMeta,
  ) => {
    const detectedEditType = overrideType || pageContext;
    const eventName = ovAnalyticsEvents.editInit(detectedEditType, context);

    sendAnalytic(eventName, {
      editType: detectedEditType,
      previousPage: document.referrer || 'direct',
      timestamp: new Date().toISOString(),
      ...additionalData,
    });
  };

  /**
   * Track when edit is completed successfully
   * Use this when:
   * - Edit form is submitted successfully
   * - Edit modal is closed after successful save
   * - Navigation back from edit page occurs
   */
  const trackEditComplete = (
    overrideType?: string,
    context?: string,
    additionalData?: AnalyticsMeta,
  ) => {
    const detectedEditType = overrideType || pageContext;
    const eventName = ovAnalyticsEvents.editComplete(detectedEditType, context);

    sendAnalytic(eventName, {
      editType: detectedEditType,
      previousPage: document.referrer || 'direct',
      timestamp: new Date().toISOString(),
      ...additionalData,
    });
  };

  return {
    trackEditInit,
    trackEditComplete,
  };
};
